import { useState, useEffect } from 'react';
import axios from 'axios';
import { formatCurrency, getCurrencySymbol } from '../utils/currency';

const API = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
  ? 'http://localhost:8001/api'
  : '/api';

// TL ile işlem gören kategoriler
const TL_CATEGORIES = ['BIST', 'BIST 100', 'BIST 30', 'BIST_100', 'BIST_30', 'BIST YILDIZ', 'BIST ANA', 'BORSA', 'HISSE', 'VIOP', 'DOVIZ', 'FOREX'];

// Varlık kategorisine göre para birimi belirle
const getAssetCurrency = (asset) => {
  if (!asset) return 'TRY';
  if (asset.currency) return asset.currency;
  return TL_CATEGORIES.includes(asset.category) ? 'TRY' : 'USD';
};

// Fiyat için para birimi simgesi (varlığın kendi para birimi)
const getPriceSymbol = (asset) => {
  const currency = getAssetCurrency(asset);
  return currency === 'USD' ? '$' : '₺';
};

function UserDetailManagement({ user, onClose, onRefresh }) {
  const [activeTab, setActiveTab] = useState('info');
  const [positions, setPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [alertModal, setAlertModal] = useState({ show: false, message: '', type: 'info' });
  
  // 🔄 Canlı kullanıcı verisi - otomatik güncellenir
  const [liveUserData, setLiveUserData] = useState(null);
  
  // Aktivite Logları
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  
  // Yatırım/Çekim Kayıtları
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [depositsLoading, setDepositsLoading] = useState(false);
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [showAddWithdrawal, setShowAddWithdrawal] = useState(false);
  const [depositForm, setDepositForm] = useState({ amount: '', note: '', status: 'APPROVED' });
  const [withdrawalForm, setWithdrawalForm] = useState({ amount: '', note: '', status: 'APPROVED' });

  const showAlert = (message, type = 'info') => setAlertModal({ show: true, message, type });
  
  // Aktif kullanıcı verisi (canlı veya props'tan gelen)
  const activeUser = liveUserData || user;
  
  // User Info Edit
  const [isEditingUserInfo, setIsEditingUserInfo] = useState(false);
  const [userInfoForm, setUserInfoForm] = useState({
    name: '',
    surname: '',
    tc_no: '',
    phone: '',
    email: '',
    account_type: 'STANDART'
  });
  
  // Forms
  const [showAddPosition, setShowAddPosition] = useState(false);
  const [showAddClosed, setShowAddClosed] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [editingClosed, setEditingClosed] = useState(null);
  const [currentPrices, setCurrentPrices] = useState({});
  
  const [positionForm, setPositionForm] = useState({
    asset_id: '',
    tutar_tl: '',           // İŞ A.2: TL cinsi teminat (yeni matematik)
    yon: 'LONG',            // LONG / SHORT
    stop_loss: '',
    take_profit: ''
  });
  
  // Arama state'i
  const [assetSearch, setAssetSearch] = useState('');
  
  // Kar/Zarar önizleme — YENİ MATEMATİK
  //   adet = tutar_tl × L / fiyat_try  (backend motoruyla aynı)
  //   pnl önizlemesi backend'den estimate-pnl ucuyla alınabilir; burada
  //   yalnız tahmini görüntü için basit hesap yapılır.
  const calculatePnLPreview = () => {
    if (!positionForm.asset_id || !positionForm.tutar_tl) return null;
    const tutarTl = parseFloat(positionForm.tutar_tl);
    if (!(tutarTl > 0)) return null;

    const asset = assets.find(a => a.id === positionForm.asset_id);
    if (!asset) return null;
    const currentPrice = currentPrices[positionForm.asset_id] || asset.current_price || 0;

    // Grup ve kaldıraç bilgi olarak asset.category üzerinden (backend değerlerine güven).
    const kaldirac = asset.leverage_hint || asset.group_leverage || null;
    const grup = asset.group_hint || asset.grup || asset.category || '';

    const assetCurrency = getAssetCurrency(asset);
    const isUsd = assetCurrency === 'USD';

    return {
      currentPrice,
      kaldirac,
      grup,
      isUsd,
      assetCurrency,
    };
  };
  
  const [closedForm, setClosedForm] = useState({
    asset_id: '',
    amount: '',
    entry_price: '',
    exit_price: '',
    profit_loss: '',
    entry_date: new Date().toISOString().split('T')[0]
  });
  
  const token = localStorage.getItem('token');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };
  
  useEffect(() => {
    // User değiştiğinde verileri yeniden çek
    if (user?.id) {
      console.log('📊 UserDetailManagement: Loading data for user:', user.id, user.email);
      fetchData();
      fetchAssets();
      // Form'u da güncelle
      setUserInfoForm({
        name: user?.name || '',
        surname: user?.surname || '',
        tc_no: user?.tc_no || '',
        phone: user?.phone || '',
        email: user?.email || '',
        account_type: user?.account_type || 'STANDART'
      });
    }
  }, [user?.id]); // user.id değiştiğinde yeniden çalış
  
  // 🔄 Otomatik yenileme - her 5 saniyede bir pozisyonları ve fiyatları güncelle
  useEffect(() => {
    if (!user?.id) return;
    
    const interval = setInterval(() => {
      console.log(' Auto-refresh: Updating positions and prices...');
      fetchData();
      fetchAssets();
      // Kullanıcı verisini de yenile
      if (onRefresh) {
        onRefresh();
      }
    }, 5000); // 5 saniye
    
    return () => clearInterval(interval);
  }, [user?.id]);
  
  // Aktivite logları sekmesi açılınca verileri çek
  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityLogs();
    }
    if (activeTab === 'deposits') {
      fetchDepositsWithdrawals();
    }
  }, [activeTab]);
  
  // Auto-calculate profit/loss when entry_price, exit_price, or amount changes
  useEffect(() => {
    const { entry_price, exit_price, amount } = closedForm;
    
    // Only calculate if all three values are present and valid
    if (entry_price && exit_price && amount) {
      const entryPrice = parseFloat(entry_price);
      const exitPrice = parseFloat(exit_price);
      const qty = parseFloat(amount);
      
      if (!isNaN(entryPrice) && !isNaN(exitPrice) && !isNaN(qty)) {
        const calculatedPnL = (exitPrice - entryPrice) * qty;
        setClosedForm(prev => ({
          ...prev,
          profit_loss: calculatedPnL.toFixed(2)
        }));
      }
    }
  }, [closedForm.entry_price, closedForm.exit_price, closedForm.amount]);
  
  const fetchData = async () => {
    setLoading(true);
    try {
      const [posRes, txRes, userRes] = await Promise.all([
        axios.get(`${API}/admin/user-positions/${user.id}`, axiosConfig),
        axios.get(`${API}/admin/user-transactions/${user.id}`, axiosConfig),
        axios.get(`${API}/admin/users/${user.id}`, axiosConfig)
      ]);
      setPositions(posRes.data || []);
      setTransactions(txRes.data || []);
      
      // Canlı kullanıcı verisini güncelle
      if (userRes.data) {
        setLiveUserData(userRes.data);
        console.log(' Live user data updated:', userRes.data.balance, userRes.data.locked_balance);
      }
    } catch (err) {
      console.error('Error:', err);
    }
    setLoading(false);
  };
  
  const fetchAssets = async () => {
    try {
      const res = await axios.get(`${API}/assets`, axiosConfig);
      console.log(' UserDetailManagement: Assets fetched:', res.data?.length, 'assets');
      setAssets(res.data || []);
      
      // Güncel fiyatları kaydet
      const prices = {};
      (res.data || []).forEach(asset => {
        prices[asset.id] = asset.current_price;
      });
      setCurrentPrices(prices);
    } catch (err) {
      console.error(' UserDetailManagement: Assets fetch error:', err);
    }
  };
  
  // Aktivite loglarını getir
  const fetchActivityLogs = async () => {
    setActivityLoading(true);
    try {
      const res = await axios.get(`${API}/admin/user-activity-logs/${user.id}?limit=100`, axiosConfig);
      setActivityLogs(res.data || []);
    } catch (err) {
      console.error(' Activity logs fetch error:', err);
      setActivityLogs([]);
    }
    setActivityLoading(false);
  };
  
  // Yatırım/Çekim kayıtlarını getir
  const fetchDepositsWithdrawals = async () => {
    setDepositsLoading(true);
    try {
      const [depositsRes, withdrawalsRes] = await Promise.all([
        axios.get(`${API}/admin/users/${user.id}/deposits`, axiosConfig),
        axios.get(`${API}/admin/users/${user.id}/withdrawals`, axiosConfig)
      ]);
      setDeposits(depositsRes.data || []);
      setWithdrawals(withdrawalsRes.data || []);
    } catch (err) {
      console.error('Deposits/Withdrawals fetch error:', err);
      setDeposits([]);
      setWithdrawals([]);
    }
    setDepositsLoading(false);
  };
  
  // Yatırım kaydı ekle (bakiye etkilenmez)
  const handleAddDeposit = async () => {
    if (!depositForm.amount || parseFloat(depositForm.amount) <= 0) {
      showAlert('Geçerli bir tutar girin!', 'error');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${user.id}/deposits/add`, {
        amount: parseFloat(depositForm.amount),
        note: depositForm.note || '',
        status: depositForm.status || 'APPROVED'
      }, axiosConfig);
      showAlert('Yatırım kaydı eklendi (bakiye etkilenmedi)', 'success');
      setShowAddDeposit(false);
      setDepositForm({ amount: '', note: '', status: 'APPROVED' });
      fetchDepositsWithdrawals();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Çekim kaydı ekle (bakiye etkilenmez)
  const handleAddWithdrawal = async () => {
    if (!withdrawalForm.amount || parseFloat(withdrawalForm.amount) <= 0) {
      showAlert('Geçerli bir tutar girin!', 'error');
      return;
    }
    try {
      await axios.post(`${API}/admin/users/${user.id}/withdrawals/add`, {
        amount: parseFloat(withdrawalForm.amount),
        note: withdrawalForm.note || '',
        status: withdrawalForm.status || 'APPROVED'
      }, axiosConfig);
      showAlert('Çekim kaydı eklendi (bakiye etkilenmedi)', 'success');
      setShowAddWithdrawal(false);
      setWithdrawalForm({ amount: '', note: '', status: 'APPROVED' });
      fetchDepositsWithdrawals();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Yatırım kaydı sil
  const handleDeleteDeposit = async (depositId) => {
    try {
      await axios.delete(`${API}/admin/deposits/${depositId}`, axiosConfig);
      showAlert('Yatırım kaydı silindi', 'success');
      fetchDepositsWithdrawals();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Çekim kaydı sil
  const handleDeleteWithdrawal = async (withdrawalId) => {
    try {
      await axios.delete(`${API}/admin/withdrawals/${withdrawalId}`, axiosConfig);
      showAlert('Çekim kaydı silindi', 'success');
      fetchDepositsWithdrawals();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Update user info
  const handleUpdateUserInfo = async () => {
    try {
      await axios.put(`${API}/admin/users/${user.id}/update`, userInfoForm, axiosConfig);
      showAlert('Kullanıcı bilgileri güncellendi!', 'success');
      setIsEditingUserInfo(false);
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Add position — İŞ A.2: yalnız tutar_tl + yon (kullanıcı yoluyla aynı)
  const handleAddPosition = async () => {
    if (!positionForm.asset_id || !positionForm.tutar_tl) {
      showAlert('Varlık ve Yatırım Tutarı (TL) zorunlu!', 'error');
      return;
    }
    const tutarTl = parseFloat(positionForm.tutar_tl);
    if (!(tutarTl > 0)) {
      showAlert('Yatırım Tutarı 0 dan büyük olmalı', 'error');
      return;
    }
    
    try {
      await axios.post(`${API}/admin/add-position`, {
        user_id: user.id,
        asset_id: positionForm.asset_id,
        tutar_tl: tutarTl,
        yon: positionForm.yon || 'LONG',
        stop_loss: positionForm.stop_loss ? parseFloat(positionForm.stop_loss) : null,
        take_profit: positionForm.take_profit ? parseFloat(positionForm.take_profit) : null
      }, axiosConfig);
      
      showAlert('Pozisyon eklendi!', 'success');
      setShowAddPosition(false);
      setPositionForm({ asset_id: '', tutar_tl: '', yon: 'LONG', stop_loss: '', take_profit: '' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object' ? (detail.message || JSON.stringify(detail)) : (detail || err.message);
      showAlert('Hata: ' + msg, 'error');
    }
  };
  
  // Edit position — Paket 3.2: position_id ile eşleşir (display-only alanlar)
  const handleEditPosition = async () => {
    if (!editingPosition) return;
    
    try {
      await axios.put(`${API}/admin/update-position`, {
        position_id: editingPosition.id,
        stop_loss: positionForm.stop_loss ? parseFloat(positionForm.stop_loss) : null,
        take_profit: positionForm.take_profit ? parseFloat(positionForm.take_profit) : null
      }, axiosConfig);
      
      showAlert('Pozisyon güncellendi!', 'success');
      setEditingPosition(null);
      setPositionForm({ asset_id: '', tutar_tl: '', yon: 'LONG', stop_loss: '', take_profit: '' });
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Delete position
  const handleDeletePosition = async (positionId, assetSymbol) => {
    
    try {
      await axios.delete(`${API}/admin/delete-position/${user.id}/${positionId}`, axiosConfig);
      showAlert('Pozisyon silindi!', 'success');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Add closed transaction
  const handleAddClosed = async () => {
    if (!closedForm.asset_id || !closedForm.amount || !closedForm.profit_loss) {
      showAlert('Varlık, Miktar ve Kar/Zarar alanları zorunludur', 'error');
      return;
    }

    try {
      if (editingClosed) {
        // DÜZENLEME
        await axios.put(`${API}/admin/update-completed-transaction`, {
          transaction_id: editingClosed.id,
          user_id: user.id,
          asset_id: closedForm.asset_id,
          amount: parseFloat(closedForm.amount),
          entry_price: parseFloat(closedForm.entry_price) || 0,
          exit_price: parseFloat(closedForm.exit_price) || 0,
          profit_loss: parseFloat(closedForm.profit_loss),
          entry_date: closedForm.entry_date || null,
          old_profit_loss: editingClosed.profit_loss
        }, axiosConfig);

        showAlert('Tamamlanmış işlem güncellendi', 'success');
      } else {
        // YENİ EKLEME
        await axios.post(`${API}/admin/add-completed-transaction`, {
          user_id: user.id,
          asset_id: closedForm.asset_id,
          amount: parseFloat(closedForm.amount),
          entry_price: parseFloat(closedForm.entry_price) || 0,
          exit_price: parseFloat(closedForm.exit_price) || 0,
          profit_loss: parseFloat(closedForm.profit_loss),
          entry_date: closedForm.entry_date || null
        }, axiosConfig);

        showAlert('Tamamlanmış işlem eklendi', 'success');
      }

      setClosedForm({ asset_id: '', amount: '', entry_price: '', exit_price: '', profit_loss: '', entry_date: '' });
      setShowAddClosed(false);
      setEditingClosed(null);
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };

  const handleDeleteClosed = async (txId) => {

    try {
      await axios.delete(`${API}/admin/delete-completed-transaction/${user.id}/${txId}`, axiosConfig);
      showAlert('İşlem silindi', 'success');
      fetchData();
      if (onRefresh) onRefresh();
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  // Update password
  const handleUpdatePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      showAlert('Şifre en az 6 karakter olmalı!', 'error');
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${user.id}/password`, 
        { new_password: newPassword }, 
        axiosConfig
      );
      showAlert('Şifre güncellendi!', 'success');
      setNewPassword('');
    } catch (err) {
      showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
    }
  };
  
  const openPositions = positions.filter(p => p.amount > 0);
  const closedTxs = transactions.filter(tx => 
    tx.transaction_type && (
      tx.transaction_type.includes('CLOSE') || 
      tx.transaction_type === 'SELL'
    )
  );
  
  const totalOpenPnL = openPositions.reduce((sum, p) => sum + (p.profit_loss || 0), 0);
  const totalClosedPnL = closedTxs.reduce((sum, tx) => sum + (tx.profit_loss || 0), 0);
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-gray-800 rounded-none sm:rounded-lg w-full h-full sm:max-w-7xl sm:h-auto sm:max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-3 sm:p-6 flex justify-between items-center z-10">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold truncate">👤 {user.email}</h2>
            <div className="flex flex-wrap gap-2 text-xs sm:text-sm">
              <span className="text-yellow-400 font-mono">ID: {user.id}</span>
              <span className="text-gray-400">•</span>
              <span className="text-green-400">Bakiye: ₺{user.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-3xl font-bold ml-2 flex-shrink-0">×</button>
        </div>
        
        {/* Tabs */}
        <div className="flex overflow-x-auto border-b border-gray-700 px-2 sm:px-6 scrollbar-hide">
          <button onClick={() => setActiveTab('info')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'info' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📋 Bilgiler</button>
          <button onClick={() => setActiveTab('positions')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'positions' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📈 Açık Pozisyonlar ({openPositions.length})</button>
          <button onClick={() => setActiveTab('closed')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'closed' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>✅ Tamamlananlar ({closedTxs.length})</button>
          <button onClick={() => setActiveTab('credit')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'credit' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>💳 Kredi</button>
          <button onClick={() => setActiveTab('password')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'password' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>🔐 Şifre</button>
          <button onClick={() => setActiveTab('activity')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'activity' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>📜 Aktivite Logları</button>
          <button onClick={() => setActiveTab('deposits')} className={`px-3 sm:px-6 py-3 font-semibold text-sm sm:text-base whitespace-nowrap ${activeTab === 'deposits' ? 'text-blue-400 border-b-2 border-blue-400' : 'text-gray-400'}`}>💰 Yatırım/Çekim</button>
        </div>
        
        {/* Content */}
        <div className="p-3 sm:p-6">
          {activeTab === 'info' && (
            <div className="space-y-4 sm:space-y-6">
              <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                <div className="flex justify-between items-center mb-3 sm:mb-4">
                  <h3 className="text-base sm:text-lg font-bold">Kullanıcı Bilgileri</h3>
                  <button 
                    onClick={() => {
                      if (isEditingUserInfo) {
                        setIsEditingUserInfo(false);
                      } else {
                        setUserInfoForm({
                          name: user?.name || '',
                          surname: user?.surname || '',
                          tc_no: user?.tc_no || '',
                          phone: user?.phone || '',
                          email: user?.email || '',
                          account_type: user?.account_type || 'STANDART'
                        });
                        setIsEditingUserInfo(true);
                      }
                    }}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium ${isEditingUserInfo ? 'bg-gray-600 hover:bg-gray-500' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {isEditingUserInfo ? '❌ İptal' : '✏️ Düzenle'}
                  </button>
                </div>
                
                {isEditingUserInfo ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Ad</label>
                        <input
                          type="text"
                          value={userInfoForm.name}
                          onChange={(e) => setUserInfoForm({...userInfoForm, name: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Soyad</label>
                        <input
                          type="text"
                          value={userInfoForm.surname}
                          onChange={(e) => setUserInfoForm({...userInfoForm, surname: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Email</label>
                        <input
                          type="email"
                          value={userInfoForm.email}
                          onChange={(e) => setUserInfoForm({...userInfoForm, email: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">📱 Telefon</label>
                        <input
                          type="text"
                          value={userInfoForm.phone}
                          onChange={(e) => setUserInfoForm({...userInfoForm, phone: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                          placeholder="5XX XXX XXXX"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">🆔 TC Kimlik No</label>
                        <input
                          type="text"
                          value={userInfoForm.tc_no}
                          onChange={(e) => setUserInfoForm({...userInfoForm, tc_no: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                          maxLength={11}
                          placeholder="TC Kimlik Numarası"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Hesap Tipi</label>
                        <select
                          value={userInfoForm.account_type}
                          onChange={(e) => setUserInfoForm({...userInfoForm, account_type: e.target.value})}
                          className="w-full bg-gray-600 text-white px-3 py-2 rounded-lg border border-gray-500 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="STANDART">Standart</option>
                          <option value="VIP">VIP</option>
                          <option value="KOMISYONSUZ">Komisyonsuz</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={handleUpdateUserInfo}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-medium"
                      >
                        💾 Kaydet
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div><div className="text-sm text-gray-400">Email</div><div className="font-semibold">{activeUser.email}</div></div>
                    <div><div className="text-sm text-gray-400">Ad Soyad</div><div className="font-semibold">{activeUser.full_name || activeUser.name || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">📱 Telefon</div><div className="font-semibold">{activeUser.phone || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">🆔 TC No</div><div className="font-semibold">{activeUser.tc_no || '-'}</div></div>
                    <div><div className="text-sm text-gray-400">Bakiye</div><div className="font-semibold text-green-400">₺{activeUser.balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</div></div>
                    <div><div className="text-sm text-gray-400">Hesap Tipi</div><div className="font-semibold">{activeUser.account_type || 'STANDART'}</div></div>
                    <div>
                      <div className="text-sm text-gray-400">💰 Kullanılabilir Marjin</div>
                      {(() => {
                        // Paket 3.3: Frontend elle hesap YAPMAZ. Backend'in verdiği
                        // pos.profit_loss (motor calculate_upnl) ve pos.locked_amount
                        // (motor) değerlerine güven. Kaldıraç çarpımı YOK.
                        const openPos = positions.filter(p => (p.amount || 0) !== 0);

                        let usedMargin = 0;
                        let totalUnrealizedPnL = 0;
                        openPos.forEach(pos => {
                          usedMargin += Number(pos.locked_amount || 0);
                          totalUnrealizedPnL += Number(pos.profit_loss || 0);
                        });

                        // Varlık = Bakiye + Açık K/Z (kredi hariç — /api/account-summary spec'i ile aynı)
                        const varlik = Number(activeUser.balance || 0) + totalUnrealizedPnL;
                        // İşlem Gücü = Varlık − Kullanılan Teminat + Kredi
                        const islemGucu = Math.max(0, varlik - usedMargin + Number(activeUser.credit || 0));
                        const marginPercent = usedMargin > 0 ? (varlik / usedMargin) * 100 : 100;

                        let bgColor = 'bg-green-500';
                        let textColor = 'text-white';
                        if (marginPercent <= 0) bgColor = 'bg-black';
                        else if (marginPercent < 50) bgColor = 'bg-red-600';
                        else if (marginPercent < 100) bgColor = 'bg-orange-500';
                        else if (marginPercent < 200) { bgColor = 'bg-yellow-500'; textColor = 'text-black'; }

                        return (
                          <div className="font-semibold flex flex-col gap-1" data-testid="admin-user-margin-summary">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`${totalUnrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                ₺{islemGucu.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded font-bold ${bgColor} ${textColor}`}>
                                %{marginPercent.toFixed(1)}
                              </span>
                            </div>
                            {openPos.length > 0 && (
                              <div className="text-xs text-gray-400">
                                Varlık: ₺{varlik.toLocaleString('tr-TR', {minimumFractionDigits: 2})} |
                                Teminat: ₺{usedMargin.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                                <span className={totalUnrealizedPnL >= 0 ? 'text-green-400' : 'text-red-400'}>
                                  {' '}({totalUnrealizedPnL >= 0 ? '+' : ''}₺{totalUnrealizedPnL.toLocaleString('tr-TR', {minimumFractionDigits: 2})} K/Z)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div>
                      <div className="text-sm text-gray-400">🏦 Kredi</div>
                      <div className="font-semibold text-purple-400">
                        ₺{(activeUser.credit || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <div className="text-sm text-gray-400">🔒 Bloke Tutar</div>
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${(activeUser.locked_balance || 0) > 0 ? 'text-yellow-400' : 'text-green-400'}`}>
                          ₺{(activeUser.locked_balance || 0).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                        </span>
                        {((activeUser.locked_balance || 0) > 0 || (positions && positions.length > 0)) && (
                          <button
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('token');
                                const res = await axios.post(`${API}/admin/users/${user.id}/recalculate-locked-balance`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                const data = res.data;
                                showAlert(`Bloke tutar düzeltildi!\nEski: ₺${data.old_locked_balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}\nYeni: ₺${data.new_locked_balance?.toLocaleString('tr-TR', {minimumFractionDigits: 2})}\nAçık Pozisyon: ${data.open_positions_count}`, 'success');
                                // Kullanıcı verisini güncelle - hemen fetchData çağır
                                fetchData();
                                if (onRefresh) {
                                  setTimeout(() => onRefresh(), 500);
                                }
                              } catch (err) {
                                showAlert('Hata: ' + (err.response?.data?.detail || err.message), 'error');
                              }
                            }}
                            className="px-2 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs font-semibold"
                            title="Bloke tutarı açık pozisyonlara göre yeniden hesapla"
                            data-testid="recalculate-locked-balance-btn"
                          >
                            🔄 Yeniden Hesapla
                          </button>
                        )}
                        {(activeUser.locked_balance || 0) === 0 && positions.length === 0 && (
                          <span className="text-xs text-green-500">✓ Temiz</span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4">İstatistikler</h3>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold">{transactions.length}</div>
                    <div className="text-xs sm:text-sm text-gray-400">Toplam İşlem</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className="text-lg sm:text-2xl font-bold">{openPositions.length}</div>
                    <div className="text-xs sm:text-sm text-gray-400">Açık Pozisyon</div>
                  </div>
                  <div className="bg-gray-600 rounded-lg p-2 sm:p-4 text-center">
                    <div className={`text-lg sm:text-2xl font-bold ${totalOpenPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {totalOpenPnL >= 0 ? '+' : ''}₺{totalOpenPnL.toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                    </div>
                    <div className="text-xs sm:text-sm text-gray-400">Toplam Kar/Zarar</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'positions' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-bold">Açık Pozisyonlar</h3>
                <button onClick={() => { setShowAddPosition(!showAddPosition); setEditingPosition(null); }} className="px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold text-sm sm:text-base whitespace-nowrap">
                  {showAddPosition ? '❌ İptal' : '➕ Ekle'}
                </button>
              </div>
              
              {(showAddPosition || editingPosition) && (
                <div className="bg-gray-700 rounded-lg p-3 sm:p-6">
                  <h4 className="font-bold mb-3 sm:mb-4 text-sm sm:text-base">{editingPosition ? '📝 Pozisyon Düzenle' : '✨ Yeni Pozisyon Ekle'}</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">🔍 Varlık Ara ve Seç *</label>
                      {!editingPosition && (
                        <input
                          type="text"
                          value={assetSearch}
                          onChange={(e) => setAssetSearch(e.target.value)}
                          className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base mb-2"
                          placeholder="Hisse adı yazın... (örn: GARAN, BTC, GOLD, THYAO)"
                        />
                      )}
                      <select 
                        value={positionForm.asset_id} 
                        onChange={(e) => {
                          setPositionForm({
                            ...positionForm, 
                            asset_id: e.target.value,
                          });
                        }} 
                        className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" 
                        disabled={!!editingPosition}
                        size={editingPosition ? 1 : 6}
                        data-testid="admin-add-position-asset-select"
                      >
                        <option value="">-- Varlık Seçin --</option>
                        {assets
                          .filter(a => 
                            assetSearch === '' || 
                            a.symbol?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.name?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.category?.toLowerCase().includes(assetSearch.toLowerCase())
                          )
                          .slice(0, 50)
                          .map(a => (
                            <option key={a.id} value={a.id}>
                              {a.symbol} ({a.category}) - {a.currency === 'USD' ? '$' : '₺'}{(a.current_price || 0).toLocaleString(a.currency === 'USD' ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </option>
                          ))
                        }
                      </select>
                      {assetSearch && !editingPosition && (
                        <p className="text-xs text-gray-400 mt-1">
                          {assets.filter(a => 
                            a.symbol?.toLowerCase().includes(assetSearch.toLowerCase()) ||
                            a.name?.toLowerCase().includes(assetSearch.toLowerCase())
                          ).length} sonuç bulundu
                        </p>
                      )}
                    </div>
                    {/* İŞ A.2 + İŞ B: Yatırım Tutarı (TL) + Yön; kaldıraç grup bilgi kutusundan gösterilir */}
                    {!editingPosition && (
                      <>
                        <div>
                          <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Yatırım Tutarı (TL) *</label>
                          <input
                            type="number"
                            step="0.01"
                            value={positionForm.tutar_tl}
                            onChange={(e) => setPositionForm({...positionForm, tutar_tl: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base"
                            placeholder="1000.00"
                            data-testid="admin-add-position-tutar-tl-input"
                          />
                          <p className="text-[10px] text-gray-500 mt-1">Kullanıcı yolundaki gibi TL cinsi teminat tutarı</p>
                        </div>
                        <div>
                          <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Yön *</label>
                          <select
                            value={positionForm.yon}
                            onChange={(e) => setPositionForm({...positionForm, yon: e.target.value})}
                            className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base"
                            data-testid="admin-add-position-yon-select"
                          >
                            <option value="LONG">📈 LONG (Alış)</option>
                            <option value="SHORT">📉 SHORT (Satış — hisse dışı)</option>
                          </select>
                        </div>
                      </>
                    )}
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Stop Loss (opsiyonel)</label>
                      <input type="number" step="0.01" value={positionForm.stop_loss} onChange={(e) => setPositionForm({...positionForm, stop_loss: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="Fiyat seviyesi" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Take Profit (opsiyonel)</label>
                      <input type="number" step="0.01" value={positionForm.take_profit} onChange={(e) => setPositionForm({...positionForm, take_profit: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="Fiyat seviyesi" />
                    </div>
                  </div>
                  
                  {/* İŞ A.2 + İŞ B: Salt-okunur grup/kaldıraç/para birimi bilgi kutusu */}
                  {positionForm.asset_id && (() => {
                    const selectedAsset = assets.find(a => a.id === positionForm.asset_id);
                    if (!selectedAsset) return null;
                    const assetCurrency = selectedAsset.currency || (getAssetCurrency(selectedAsset));
                    const priceSymbol = assetCurrency === 'USD' ? '$' : '₺';
                    const isUsd = assetCurrency === 'USD';
                    const category = selectedAsset.category || '';
                    return (
                      <div className="mt-4 bg-blue-900/30 border border-blue-500/40 rounded-lg p-4" data-testid="admin-add-position-info-box">
                        <h5 className="text-blue-300 font-semibold mb-2 text-sm">ℹ️ Enstrüman Bilgileri (salt-okunur)</h5>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-gray-400">Sembol:</span>
                            <span className="text-white ml-2 font-mono">{selectedAsset.symbol}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Kategori:</span>
                            <span className="text-white ml-2">{category}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Para Birimi:</span>
                            <span className="text-cyan-300 ml-2 font-semibold">{assetCurrency}</span>
                          </div>
                          <div>
                            <span className="text-gray-400">Güncel Fiyat:</span>
                            <span className="text-cyan-400 ml-2 font-semibold">
                              {priceSymbol}{(selectedAsset.current_price || 0).toLocaleString(isUsd ? 'en-US' : 'tr-TR', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </span>
                          </div>
                          <div className="col-span-2 pt-2 border-t border-blue-500/30">
                            <div className="text-yellow-300 text-xs">
                              🎯 Kaldıraç ve grup <strong>backend GROUP_LEVERAGE tablosundan</strong> gelir — admin seçemez. Motor pozisyon açarken uygular.
                            </div>
                            {isUsd && (
                              <div className="text-orange-300 text-xs mt-1">
                                💱 USD bazlı: girdiğiniz tutar TL cinsindendir. Motor kur_acilis'i (USD/TRY) otomatik uygular.
                              </div>
                            )}
                          </div>
                          {positionForm.tutar_tl && parseFloat(positionForm.tutar_tl) > 0 && (
                            <div className="col-span-2 pt-2 border-t border-blue-500/30">
                              <span className="text-gray-400">Yatırım (Teminat):</span>
                              <span className="text-green-400 font-bold text-lg ml-2">
                                ₺{parseFloat(positionForm.tutar_tl).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                              </span>
                              <span className="text-gray-500 text-xs ml-2">({positionForm.yon})</span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  <button
                    onClick={editingPosition ? handleEditPosition : handleAddPosition}
                    className="w-full mt-3 sm:mt-4 py-2 sm:py-3 bg-blue-600 text-sm sm:text-base hover:bg-blue-700 rounded-lg font-semibold"
                    data-testid="admin-add-position-submit-btn"
                  >
                    {editingPosition ? '💾 Güncelle' : '➕ Ekle'}
                  </button>
                </div>
              )}
              
              {openPositions.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-6 sm:p-8 text-center text-gray-400 text-sm sm:text-base">Açık pozisyon yok</div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {openPositions.map(pos => {
                    // USD kategorileri - API'den gelen category veya asset_category kullan
                    const usdCategories = ['EMTIA', 'KRIPTO', 'ABD_NASDAQ', 'US_STOCKS', 'FOREX'];
                    const isUsdAsset = usdCategories.includes(pos.category) || usdCategories.includes(pos.asset_category);
                    const priceSymbol = isUsdAsset ? '$' : '₺';
                    
                    return (
                    <div key={pos.id} className="bg-gray-700 rounded-lg p-3 sm:p-4">
                      <div className="flex justify-between items-start mb-2 sm:mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-lg sm:text-xl font-bold">{pos.asset_symbol}</span>
                            {isUsdAsset && <span className="text-xs bg-blue-600 px-2 py-0.5 rounded">USD</span>}
                          </div>
                          <div className="text-xs text-gray-400">
                            {pos.grup ? `Grup: ${pos.grup}` : (pos.category || pos.asset_category || '')}
                            {pos.leverage && pos.leverage > 1 && (
                              <span className="ml-2">· 1:{pos.leverage} (grup)</span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                          <button onClick={() => { setEditingPosition(pos); setPositionForm({ asset_id: pos.asset_id, tutar_tl: '', yon: pos.yon || 'LONG', stop_loss: pos.stop_loss?.toString() || '', take_profit: pos.take_profit?.toString() || '' }); setShowAddPosition(false); }} className="px-2 sm:px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs sm:text-sm">📝</button>
                          <button 
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/close-position/${pos.id}`, {}, axiosConfig);
                                showAlert(`Pozisyon kapatıldı!\n\nKar/Zarar: ${formatCurrency(res.data.pnl, user)}\nYeni Bakiye: ${formatCurrency(res.data.new_balance, user)}`, 'success');
                                await fetchData();
                                if (onRefresh) await onRefresh();
                              } catch (err) {
                                showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                              }
                            }}
                            className="px-2 sm:px-3 py-1 bg-orange-600 hover:bg-orange-700 rounded text-xs sm:text-sm"
                          >
                            Kapat
                          </button>
                          <button onClick={() => handleDeletePosition(pos.id, pos.asset_symbol)} className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs sm:text-sm">❌</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm">
                        <div><div className="text-gray-400">Miktar</div><div className="font-semibold">{pos.amount}</div></div>
                        <div><div className="text-gray-400">Ort. Alış</div><div className="font-semibold">{priceSymbol}{pos.avg_buy_price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Güncel</div><div className="font-semibold">{priceSymbol}{pos.current_price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Kar/Zarar</div><div className={`font-semibold ${pos.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.profit_loss >= 0 ? '+' : ''}₺{pos.profit_loss?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">%</div><div className={`font-semibold ${pos.profit_loss_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>{pos.profit_loss_percent >= 0 ? '+' : ''}{pos.profit_loss_percent?.toFixed(2)}%</div></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'closed' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-bold">Tamamlanan Pozisyonlar</h3>
                <button onClick={() => { 
                  setShowAddClosed(!showAddClosed); 
                  setEditingClosed(null); 
                  setClosedForm({ asset_id: '', amount: '', entry_price: '', exit_price: '', profit_loss: '', entry_date: new Date().toISOString().split('T')[0] });
                }} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold">
                  {showAddClosed ? '❌ İptal' : '➕ Tamamlanmış İşlem Ekle'}
                </button>
              </div>
              
              {showAddClosed && (
                <div className="bg-gray-700 rounded-lg p-6">
                  <h4 className="font-bold mb-4">{editingClosed ? '📝 Tamamlanmış İşlem Düzenle' : '➕ Tamamlanmış İşlem Ekle'}</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Varlık *</label>
                      <select value={closedForm.asset_id} onChange={(e) => setClosedForm({...closedForm, asset_id: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base">
                        <option value="">Seçin</option>
                        {assets.map(a => (<option key={a.id} value={a.id}>{a.symbol} ({a.category})</option>))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Miktar *</label>
                      <input type="number" value={closedForm.amount} onChange={(e) => setClosedForm({...closedForm, amount: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="100" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Giriş Fiyatı</label>
                      <input type="number" step="0.01" value={closedForm.entry_price} onChange={(e) => setClosedForm({...closedForm, entry_price: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="185.50" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Çıkış Fiyatı</label>
                      <input type="number" step="0.01" value={closedForm.exit_price} onChange={(e) => setClosedForm({...closedForm, exit_price: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="195.50" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Kar/Zarar (₺) *</label>
                      <input type="number" step="0.01" value={closedForm.profit_loss} onChange={(e) => setClosedForm({...closedForm, profit_loss: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" placeholder="1000 veya -500" />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm text-gray-400 mb-1 sm:mb-2">Tarih</label>
                      <input type="date" value={closedForm.entry_date} onChange={(e) => setClosedForm({...closedForm, entry_date: e.target.value})} className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base" />
                    </div>
                  </div>
                  <button onClick={handleAddClosed} className="w-full mt-3 sm:mt-4 py-2 sm:py-3 bg-blue-600 text-sm sm:text-base hover:bg-blue-700 rounded-lg font-semibold">
                    {editingClosed ? '💾 Güncelle' : '➕ Ekle'}
                  </button>
                </div>
              )}
              
              {closedTxs.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-8 text-center text-gray-400">Tamamlanmış işlem yok</div>
              ) : (
                <div className="space-y-3">
                  {closedTxs.slice(0, 30).map(tx => {
                    // Kapanma nedenini belirle
                    const getCloseReasonBadge = () => {
                      if (tx.transaction_type === 'CLOSE_STOPOUT' || tx.close_reason === 'MARGIN_STOPOUT') {
                        return <span className="ml-2 text-xs bg-red-600 px-2 py-0.5 rounded">STOP-OUT</span>;
                      }
                      if (tx.close_reason === 'SL_TRIGGERED' || tx.transaction_type === 'CLOSE_SL') {
                        return <span className="ml-2 text-xs bg-orange-600 px-2 py-0.5 rounded">SL</span>;
                      }
                      if (tx.close_reason === 'TP_TRIGGERED' || tx.transaction_type === 'CLOSE_TP') {
                        return <span className="ml-2 text-xs bg-green-600 px-2 py-0.5 rounded">TP</span>;
                      }
                      if (tx.close_reason === 'MANUAL' || tx.transaction_type === 'CLOSE') {
                        return <span className="ml-2 text-xs bg-blue-600 px-2 py-0.5 rounded">MANUEL</span>;
                      }
                      return null;
                    };
                    
                    return (
                    <div key={tx.id} className="bg-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <div className="flex items-center">
                            <span className="font-bold">{tx.asset_symbol}</span>
                            {getCloseReasonBadge()}
                          </div>
                          <div className="text-xs text-gray-400">
                            {(tx.grup || tx.category) && <span>Grup: {tx.grup || tx.category}</span>}
                          </div>
                          <div className={`font-bold ${tx.profit_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {tx.profit_loss >= 0 ? '+' : ''}₺{tx.profit_loss?.toFixed(2)}
                          </div>
                        </div>
                        <div className="flex gap-1 sm:gap-2 flex-shrink-0 ml-2">
                          <button
                            data-testid={`reopen-tx-btn-${tx.id}`}
                            onClick={async () => {
                              // Paket 4: Reopen = kapanış undo. Onay diyaloğu.
                              const confirmed = window.confirm(
                                `Bu işlemin kapanışı geri alınacak.\n\n` +
                                `• Kâr/zarar (${tx.profit_loss >= 0 ? '+' : ''}₺${(tx.profit_loss || 0).toFixed(2)}) bakiyeden düşülecek\n` +
                                `• Serbest bırakılan teminat tekrar kilitlenecek\n` +
                                `• Pozisyon giriş fiyatı (${tx.entry_price || tx.price}) korunarak yeniden açılacak\n\n` +
                                `Devam etmek istiyor musunuz?`
                              );
                              if (!confirmed) return;
                              try {
                                const res = await axios.post(`${API}/reopen-position/${tx.id}`, {}, axiosConfig);
                                showAlert(
                                  `✅ Pozisyon geri açıldı (undo)\n\n` +
                                  `Giriş: ₺${(res.data.entry_price || 0).toFixed(2)}\n` +
                                  `Geri alınan K/Z: ₺${(res.data.pnl_reverted || 0).toFixed(2)}\n` +
                                  `Yeniden kilitlenen teminat: ₺${(res.data.margin_relocked || 0).toFixed(2)}\n` +
                                  `Yeni bakiye: ₺${(res.data.new_balance || 0).toFixed(2)}`,
                                  'success'
                                );
                                await fetchData();
                                if (onRefresh) await onRefresh();
                              } catch (err) {
                                const detail = err.response?.data?.detail;
                                const msg = typeof detail === 'object' ? (detail.message || JSON.stringify(detail)) : (detail || err.message);
                                showAlert(`Hata: ${msg}`, 'error');
                              }
                            }}
                            title="Kapanışı geri al (reopen undo)"
                            className="px-2 sm:px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-xs sm:text-sm"
                          >
                            🔄
                          </button>
                          <button 
                            onClick={() => {
                              setEditingClosed(tx);
                              setClosedForm({
                                asset_id: tx.asset_id,
                                amount: tx.amount.toString(),
                                entry_price: tx.entry_price?.toString() || tx.price?.toString() || '',
                                exit_price: tx.exit_price?.toString() || tx.price?.toString() || '',
                                profit_loss: tx.profit_loss.toString(),
                                entry_date: tx.timestamp ? new Date(tx.timestamp).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
                              });
                              setShowAddClosed(true);
                            }}
                            className="px-2 sm:px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-xs sm:text-sm"
                          >
                            📝
                          </button>
                          <button 
                            onClick={() => handleDeleteClosed(tx.id)}
                            className="px-2 sm:px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-xs sm:text-sm"
                          >
                            ❌
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm">
                        <div><div className="text-gray-400">Miktar</div><div>{tx.amount}</div></div>
                        <div><div className="text-gray-400">Fiyat</div><div>₺{tx.price?.toFixed(2)}</div></div>
                        <div><div className="text-gray-400">Tarih</div><div className="text-xs">{new Date(tx.timestamp).toLocaleString('tr-TR')}</div></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'credit' && (
            <div className="space-y-4">
              <div className="bg-gray-700 rounded-lg p-6">
                <h3 className="text-lg font-bold mb-4">💳 Kredi Yönetimi</h3>
                
                {/* Mevcut Kredi */}
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="text-gray-400 text-sm mb-1">Mevcut Kredi Bakiyesi</div>
                  <div className="text-2xl font-bold text-green-400">{formatCurrency(user.credit || 0, user)}</div>
                </div>
                
                {/* Kredi Ekle */}
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold mb-3">➕ Kredi Ekle</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Tutar (₺)</label>
                      <input 
                        type="number" 
                        value={creditAmount} 
                        onChange={(e) => setCreditAmount(e.target.value)} 
                        placeholder="Örn: 1000" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Açıklama (opsiyonel)</label>
                      <input 
                        type="text" 
                        value={creditDescription} 
                        onChange={(e) => setCreditDescription(e.target.value)} 
                        placeholder="Örn: Promosyon kredisi" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <button 
                      onClick={async () => {
                        if (!creditAmount || parseFloat(creditAmount) <= 0) {
                          showAlert('Geçerli bir tutar girin', 'error');
                          return;
                        }
                        try {
                          const res = await axios.post(
                            `${API}/admin/users/${user.id}/credit/add?amount=${parseFloat(creditAmount)}&description=${encodeURIComponent(creditDescription || 'Admin kredi ekledi')}`,
                            {},
                            axiosConfig
                          );
                          showAlert(`Kredi başarıyla eklendi!\nYeni Kredi: ${formatCurrency(res.data.new_credit, user)}`, 'success');
                          setCreditAmount('');
                          setCreditDescription('');
                          onRefresh();
                        } catch (err) {
                          showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                        }
                      }}
                      className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold"
                    >
                      ➕ Kredi Ekle
                    </button>
                  </div>
                </div>
                
                {/* Kredi Düş */}
                <div className="bg-gray-800 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 text-red-400">➖ Kredi Düş</h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Tutar (₺)</label>
                      <input 
                        type="number" 
                        value={creditAmount} 
                        onChange={(e) => setCreditAmount(e.target.value)} 
                        placeholder="Örn: 500" 
                        className="w-full bg-gray-600 text-white px-4 py-3 rounded-lg"
                      />
                    </div>
                    <button 
                      onClick={async () => {
                        if (!creditAmount || parseFloat(creditAmount) <= 0) {
                          showAlert('Geçerli bir tutar girin', 'error');
                          return;
                        }
                        try {
                          const res = await axios.post(
                            `${API}/admin/users/${user.id}/credit/deduct?amount=${parseFloat(creditAmount)}&description=${encodeURIComponent(creditDescription || 'Admin kredi düşürdü')}`,
                            {},
                            axiosConfig
                          );
                          showAlert(`Kredi başarıyla düşürüldü!\nYeni Kredi: ${formatCurrency(res.data.new_credit, user)}`, 'success');
                          setCreditAmount('');
                          setCreditDescription('');
                          onRefresh();
                        } catch (err) {
                          showAlert(`Hata: ${err.response?.data?.detail || err.message}`, 'error');
                        }
                      }}
                      className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold"
                    >
                      ➖ Kredi Düş
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'password' && (
            <div className="bg-gray-700 rounded-lg p-6">
              <h3 className="text-lg font-bold mb-4">🔐 Şifre Yönetimi</h3>
              <div className="space-y-4">
                {/* Mevcut Şifre Gösterimi */}
                <div className="bg-gray-800 p-4 rounded-lg border border-green-600">
                  <label className="block text-xs sm:text-sm text-gray-400 mb-2">🔑 Mevcut Şifre</label>
                  <div className="flex items-center justify-between bg-gray-900 px-4 py-3 rounded-lg">
                    <div className="w-full">
                      <span className="text-white font-mono text-lg bg-green-900/30 px-4 py-2 rounded block text-center">
                        {user.plaintext_password || '(Kayıtlı şifre yok)'}
                      </span>
                      {!user.plaintext_password && (
                        <p className="text-yellow-400 text-xs mt-2 text-center">
                          ⚠️ Bu kullanıcı eski sistemden kalmış. Yeni şifre belirleyebilirsiniz.
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Yeni Şifre */}
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
                  <label className="block text-xs sm:text-sm text-gray-400 mb-2">🔐 Yeni Şifre Belirle</label>
                  <input 
                    type="text" 
                    value={newPassword} 
                    onChange={(e) => setNewPassword(e.target.value)} 
                    placeholder="Min 6 karakter" 
                    className="w-full bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-sm sm:text-base mb-3" 
                  />
                  <button 
                    onClick={handleUpdatePassword} 
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 rounded-lg font-semibold"
                  >
                    Şifreyi Güncelle
                  </button>
                  <p className="text-gray-500 text-xs mt-2 text-center">
                    Şifre güncellenince kullanıcı yeni şifre ile giriş yapabilir
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {activeTab === 'activity' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-base sm:text-lg font-bold">📜 Kullanıcı Aktivite Logları</h3>
                <button 
                  onClick={fetchActivityLogs} 
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm"
                >
                  🔄 Yenile
                </button>
              </div>
              
              {activityLoading ? (
                <div className="text-center py-8 text-gray-400">Yükleniyor...</div>
              ) : activityLogs.length === 0 ? (
                <div className="bg-gray-700 rounded-lg p-8 text-center">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-gray-400">Henüz aktivite kaydı yok</p>
                  <p className="text-gray-500 text-sm mt-1">Kullanıcı işlem yaptıkça burada görünecek</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
                  {activityLogs.map((log, idx) => (
                    <div key={log.id || idx} className="bg-gray-700 rounded-lg p-3 hover:bg-gray-600 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="font-semibold text-sm sm:text-base">{log.action}</div>
                          <div className="text-gray-300 text-xs sm:text-sm mt-1">{log.details}</div>
                          {log.metadata && Object.keys(log.metadata).length > 0 && (
                            <div className="mt-2 text-xs text-gray-400 bg-gray-800 rounded p-2">
                              {log.metadata.asset_symbol && <span className="inline-block mr-3">📊 {log.metadata.asset_symbol}</span>}
                              {log.metadata.amount && <span className="inline-block mr-3">📦 {log.metadata.amount} adet</span>}
                              {log.metadata.price && <span className="inline-block mr-3">💰 ₺{parseFloat(log.metadata.price).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>}
                              {log.metadata.pnl !== undefined && (
                                <span className={`inline-block mr-3 ${log.metadata.pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  K/Z: {log.metadata.pnl >= 0 ? '+' : ''}₺{parseFloat(log.metadata.pnl).toLocaleString('tr-TR', {minimumFractionDigits: 2})}
                                </span>
                              )}
                              {log.metadata.leverage && log.metadata.leverage > 1 && <span className="inline-block mr-3">⚡ {log.metadata.leverage}x</span>}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 whitespace-nowrap ml-3">
                          {new Date(log.timestamp).toLocaleString('tr-TR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {activityLogs.length > 0 && (
                <div className="text-center text-gray-500 text-xs mt-2">
                  Toplam {activityLogs.length} kayıt gösteriliyor
                </div>
              )}
            </div>
          )}
          
          {/* Yatırım/Çekim Tab */}
          {activeTab === 'deposits' && (
            <div className="space-y-6">
              {/* Yatırımlar */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-green-400">💰 Yatırım Kayıtları ({deposits.length})</h3>
                  <button 
                    onClick={() => setShowAddDeposit(true)}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 rounded-lg text-sm flex items-center gap-1"
                  >
                    ➕ Yatırım Ekle
                  </button>
                </div>
                
                {/* Add Deposit Form */}
                {showAddDeposit && (
                  <div className="bg-gray-800 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold mb-3">Yeni Yatırım Kaydı (Bakiye Etkilenmez)</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400">Tutar (₺)</label>
                        <input
                          type="number"
                          value={depositForm.amount}
                          onChange={(e) => setDepositForm({...depositForm, amount: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                          placeholder="Örn: 10000"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Not (Opsiyonel)</label>
                        <input
                          type="text"
                          value={depositForm.note}
                          onChange={(e) => setDepositForm({...depositForm, note: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                          placeholder="Açıklama"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Durum</label>
                        <select
                          value={depositForm.status}
                          onChange={(e) => setDepositForm({...depositForm, status: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                        >
                          <option value="APPROVED">✓ Onaylı</option>
                          <option value="PENDING">⏳ Bekliyor</option>
                          <option value="REJECTED">✗ Reddedildi</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={handleAddDeposit} className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-sm">
                        Ekle
                      </button>
                      <button onClick={() => { setShowAddDeposit(false); setDepositForm({ amount: '', note: '', status: 'APPROVED' }); }} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm">
                        İptal
                      </button>
                    </div>
                  </div>
                )}
                
                {depositsLoading ? (
                  <div className="text-center py-4 text-gray-400">Yükleniyor...</div>
                ) : deposits.length === 0 ? (
                  <div className="text-center py-4 text-gray-400">Yatırım kaydı yok</div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {deposits.map((dep) => (
                      <div key={dep.id} className={`flex justify-between items-center rounded-lg p-3 ${
                        dep.status === 'APPROVED' ? 'bg-green-900/30 border border-green-600/30' :
                        dep.status === 'PENDING' ? 'bg-yellow-900/30 border border-yellow-600/30' :
                        dep.status === 'REJECTED' ? 'bg-red-900/30 border border-red-600/30' :
                        'bg-gray-800'
                      }`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-green-400">+₺{parseFloat(dep.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              dep.status === 'APPROVED' ? 'bg-green-600 text-white' :
                              dep.status === 'PENDING' ? 'bg-yellow-600 text-black' :
                              dep.status === 'REJECTED' ? 'bg-red-600 text-white' :
                              'bg-gray-600 text-white'
                            }`}>
                              {dep.status === 'APPROVED' ? '✓ Onaylı' : 
                               dep.status === 'PENDING' ? '⏳ Bekliyor' : 
                               dep.status === 'REJECTED' ? '✗ Reddedildi' : dep.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(dep.created_at).toLocaleString('tr-TR')}
                          </div>
                          {dep.admin_note && <div className="text-xs text-gray-500 mt-1">{dep.admin_note}</div>}
                        </div>
                        <button 
                          onClick={() => handleDeleteDeposit(dep.id)}
                          className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Çekimler */}
              <div className="bg-gray-700 rounded-lg p-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-red-400">💸 Çekim Kayıtları ({withdrawals.length})</h3>
                  <button 
                    onClick={() => setShowAddWithdrawal(true)}
                    className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm flex items-center gap-1"
                  >
                    ➕ Çekim Ekle
                  </button>
                </div>
                
                {/* Add Withdrawal Form */}
                {showAddWithdrawal && (
                  <div className="bg-gray-800 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold mb-3">Yeni Çekim Kaydı (Bakiye Etkilenmez)</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-gray-400">Tutar (₺)</label>
                        <input
                          type="number"
                          value={withdrawalForm.amount}
                          onChange={(e) => setWithdrawalForm({...withdrawalForm, amount: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                          placeholder="Örn: 5000"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Not (Opsiyonel)</label>
                        <input
                          type="text"
                          value={withdrawalForm.note}
                          onChange={(e) => setWithdrawalForm({...withdrawalForm, note: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                          placeholder="Açıklama"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-400">Durum</label>
                        <select
                          value={withdrawalForm.status}
                          onChange={(e) => setWithdrawalForm({...withdrawalForm, status: e.target.value})}
                          className="w-full bg-gray-700 rounded px-3 py-2 text-white"
                        >
                          <option value="APPROVED">✓ Tamamlandı</option>
                          <option value="PENDING">⏳ Bekliyor</option>
                          <option value="REJECTED">✗ Reddedildi</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={handleAddWithdrawal} className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm">
                        Ekle
                      </button>
                      <button onClick={() => { setShowAddWithdrawal(false); setWithdrawalForm({ amount: '', note: '', status: 'APPROVED' }); }} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-sm">
                        İptal
                      </button>
                    </div>
                  </div>
                )}
                
                {depositsLoading ? (
                  <div className="text-center py-4 text-gray-400">Yükleniyor...</div>
                ) : withdrawals.length === 0 ? (
                  <div className="text-center py-4 text-gray-400">Çekim kaydı yok</div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {withdrawals.map((wd) => (
                      <div key={wd.id} className={`flex justify-between items-center rounded-lg p-3 ${
                        wd.status === 'APPROVED' || wd.status === 'COMPLETED' ? 'bg-green-900/30 border border-green-600/30' :
                        wd.status === 'PENDING' ? 'bg-yellow-900/30 border border-yellow-600/30' :
                        wd.status === 'REJECTED' ? 'bg-red-900/30 border border-red-600/30' :
                        'bg-gray-800'
                      }`}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-red-400">-₺{parseFloat(wd.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})}</span>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              wd.status === 'APPROVED' || wd.status === 'COMPLETED' ? 'bg-green-600 text-white' :
                              wd.status === 'PENDING' ? 'bg-yellow-600 text-black' :
                              wd.status === 'REJECTED' ? 'bg-red-600 text-white' :
                              'bg-gray-600 text-white'
                            }`}>
                              {wd.status === 'APPROVED' || wd.status === 'COMPLETED' ? '✓ Tamamlandı' : 
                               wd.status === 'PENDING' ? '⏳ Bekliyor' : 
                               wd.status === 'REJECTED' ? '✗ Reddedildi' : wd.status}
                            </span>
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            {new Date(wd.created_at).toLocaleString('tr-TR')}
                          </div>
                          {wd.admin_note && <div className="text-xs text-gray-500 mt-1">{wd.admin_note}</div>}
                        </div>
                        <button 
                          onClick={() => handleDeleteWithdrawal(wd.id)}
                          className="px-2 py-1 bg-red-600 hover:bg-red-700 rounded text-xs"
                        >
                          🗑️ Sil
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="bg-yellow-900/30 border border-yellow-600/50 rounded-lg p-3">
                <p className="text-yellow-400 text-sm">
                  ⚠️ <strong>Not:</strong> Bu sekmeden yapılan ekleme/silme işlemleri kullanıcının bakiyesini <strong>ETKİLEMEZ</strong>. 
                  Sadece kayıt amaçlıdır.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Site İçi Alert Modal */}
      {alertModal.show && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm p-5 shadow-2xl">
            <div className="text-center">
              <div className="text-4xl mb-3">
                {alertModal.type === 'success' ? '✅' : alertModal.type === 'error' ? '❌' : 'ℹ️'}
              </div>
              <p className={`text-sm mb-4 whitespace-pre-line ${alertModal.type === 'success' ? 'text-green-400' : alertModal.type === 'error' ? 'text-red-400' : 'text-gray-300'}`}>
                {alertModal.message}
              </p>
              <button
                onClick={() => setAlertModal({ show: false, message: '', type: 'info' })}
                className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Tamam
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailManagement;
